<!--
.. Mofified by Kishore Jalleda
.. full list of modifications at https://github.com/unstructai
.. copyright: (c) 2023 Kishore Jalleda
.. author:: Kishore Jalleda <kjalleda@gmail.com>
-->
<template>
  <v-container fluid>
    <new-edit-sheet />
    <delete-dialog />
    <v-row align="center" justify="space-between" no-gutters>
      <v-col cols="8">
        <settings-breadcrumbs v-model="project" />
      </v-col>
      <v-col class="text-right">
        <v-btn color="info" class="mr-2" @click="createEditShow()">
          <v-icon class="mr-1">mdi-plus</v-icon>
          New Source Data Format
        </v-btn>
      </v-col>
    </v-row>
    <v-row no-gutters>
        <v-col>
          <v-alert outlined type="info" dismissible prominent icon="mdi-file-document-edit">
            Recognizing the diversity of data sources, UnStruct empowers you to specify the underlying data formats used within your environment. This feature facilitates a deeper understanding of how data is structured and stored across different platforms, enabling more effective data manipulation and analysis. Commonly supported data formats include:
            <ul>
              <li><strong>JSON:</strong> A lightweight format for storing and transporting data, easily readable by humans and machines.</li>
              <li><strong>CSV:</strong> A simple format used to store tabular data, such as spreadsheets or databases.</li>
              <li><strong>XML:</strong> A markup language that defines a set of rules for encoding documents in a format that is both human-readable and machine-readable.</li>
              <li>And more, including custom formats specific to your organizational needs.</li>
            </ul>
            <br>
            By defining common data formats, UnStruct ensures that users can effectively interact with and analyze data from various sources, enhancing the incident response process.
          </v-alert>
        </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <v-card elevation="0">
          <v-card-title>
            <v-text-field
              v-model="q"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              clearable
            />
          </v-card-title>
          <v-row>
            <v-col cols="12" sm="6" md="4" v-for="item in items" :key="item.id">
              <v-card class="mb-5">
                <v-card-title>
                  {{ item.name }}
                  <v-spacer></v-spacer>
                  <v-menu bottom left>
                    <template v-slot:activator="{ on }">
                      <v-btn icon v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item @click="createEditShow(item)">
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="removeShow(item)">
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-card-title>
                <v-card-subtitle>
                  {{ item.description }}
                </v-card-subtitle>
              </v-card>
            </v-col>
          </v-row>
          <v-data-table
            :headers="headers"
            :items="items"
            :server-items-length="total"
            :page.sync="page"
            :items-per-page.sync="itemsPerPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="descending"
            :loading="loading"
            loading-text="Loading... Please wait"
          >
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapFields } from "vuex-map-fields"
import { mapActions } from "vuex"

import SettingsBreadcrumbs from "@/components/SettingsBreadcrumbs.vue"
import DeleteDialog from "@/data/source/dataFormat/DeleteDialog.vue"
import NewEditSheet from "@/data/source/dataFormat/NewEditSheet.vue"

export default {
  name: "SourceDataFormatTable",

  components: {
    DeleteDialog,
    NewEditSheet,
    SettingsBreadcrumbs,
  },
  data() {
    return {
      headers: [],
    }
  },

  computed: {
    ...mapFields("sourceDataFormat", [
      "table.options.q",
      "table.options.page",
      "table.options.itemsPerPage",
      "table.options.sortBy",
      "table.options.descending",
      "table.options.filters.project",
      "table.loading",
      "table.rows.items",
      "table.rows.total",
    ]),
    ...mapFields("route", ["query"]),
  },

  created() {
    this.project = [{ name: this.query.project }]
    this.getAll()

    this.$watch(
      (vm) => [vm.page],
      () => {
        this.getAll()
      }
    )

    this.$watch(
      (vm) => [vm.q, vm.itemsPerPage, vm.sortBy, vm.descending, vm.project],
      () => {
        this.page = 1
        this.$router.push({ query: { project: this.project[0].name } })
        this.getAll()
      }
    )
  },

  methods: {
    ...mapActions("sourceDataFormat", ["getAll", "createEditShow", "removeShow"]),
  },
}
</script>
